import { getRouteApi } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ExploreFilterState } from "@blis/insights/explore/explore-filter-state";
import {
  defaultExploreUrlState,
  type ExploreUrlState,
  exploreSearchToUrlState,
  exploreUrlStateToNavigateSearch,
} from "@blis/insights/explore/explore-url-state";

const routeApi = getRouteApi("/_auth/_app/insights/explore");

export type ExploreUrlPatch = {
  entity?: string;
  view?: ExploreUrlState["view"];
  filterState?: ExploreUrlState["filterState"];
  summary?: Partial<ExploreUrlState["summary"]>;
};

export type ExploreEntityUrlMeta = {
  name: string;
  groupableFields: readonly string[];
  bucketableFields: ReadonlySet<string>;
  measureIds: ReadonlySet<string>;
  chartIds: ReadonlySet<string>;
};

function filterStateKey(state: ExploreFilterState): string {
  return JSON.stringify({
    dateSince: state.dateSince,
    dateUntil: state.dateUntil,
    includeCancelled: state.includeCancelled,
    columnFilters: state.columnFilters,
  });
}

function urlStatesMatch(a: ExploreUrlState, b: ExploreUrlState): boolean {
  return (
    a.entity === b.entity &&
    a.view === b.view &&
    filterStateKey(a.filterState) === filterStateKey(b.filterState) &&
    a.summary.groupField === b.summary.groupField &&
    a.summary.bucket === b.summary.bucket &&
    a.summary.measureId === b.summary.measureId &&
    a.summary.chartOverride === b.summary.chartOverride
  );
}

function mergeUrlPatch(
  base: ExploreUrlState,
  patch: ExploreUrlPatch,
): ExploreUrlState {
  return {
    ...base,
    ...patch,
    filterState: patch.filterState ?? base.filterState,
    summary: patch.summary
      ? { ...base.summary, ...patch.summary }
      : base.summary,
  };
}

export function useExploreUrlState(
  entityMeta: ExploreEntityUrlMeta | null,
  entityNames: readonly string[],
) {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const defaults = useMemo(
    () =>
      defaultExploreUrlState(
        entityMeta?.groupableFields ?? [],
        entityMeta?.bucketableFields ?? new Set(),
      ),
    [entityMeta],
  );

  const urlFromSearch = useMemo(
    () =>
      exploreSearchToUrlState(search, {
        entityNames: new Set(entityNames),
        groupableFields: entityMeta?.groupableFields ?? [],
        bucketableFields: entityMeta?.bucketableFields ?? new Set(),
        measureIds: entityMeta?.measureIds ?? new Set(["count:*"]),
        chartIds: entityMeta?.chartIds ?? new Set(["auto"]),
      }),
    [entityMeta, entityNames, search],
  );

  const [optimistic, setOptimistic] = useState<ExploreUrlState | null>(null);
  const urlFromSearchRef = useRef(urlFromSearch);
  urlFromSearchRef.current = urlFromSearch;
  const urlState = optimistic ?? urlFromSearch;

  useEffect(() => {
    if (optimistic && urlStatesMatch(optimistic, urlFromSearch)) {
      setOptimistic(null);
    }
  }, [optimistic, urlFromSearch]);

  const syncSearch = useCallback(
    (next: ExploreUrlState) => {
      const params = exploreUrlStateToNavigateSearch(next, defaults);
      navigate({
        search: (prev) => ({ ...prev, ...params }),
        replace: true,
      });
    },
    [defaults, navigate],
  );

  useEffect(() => {
    if (optimistic) {
      syncSearch(optimistic);
    }
  }, [optimistic, syncSearch]);

  const applyUrlState = useCallback((next: ExploreUrlState) => {
    setOptimistic(next);
  }, []);

  const patchUrlState = useCallback((patch: ExploreUrlPatch) => {
    setOptimistic((current) =>
      mergeUrlPatch(current ?? urlFromSearchRef.current, patch),
    );
  }, []);

  return {
    urlState,
    defaults,
    setUrlState: applyUrlState,
    patchUrlState,
  };
}
